import { useState } from 'react';
import _noop from 'lodash/noop';
import { KEY_VALUE_LAYOUT, KeyConfig } from 'commons-ui';

import usePermissionV2 from 'commons-ui/src/permission/usePermission';
import { PAGE_NAMES, RESOURCES, ACTION } from 'constant';
import useService from './useService';
import { ACTION_BUTTONS_ID, VIEW_FIELD_KEYS } from './constants';

const useBasicDetails = () => {
	const {
		getBasicUserInfo,
		getWalletDetails,
		getBlockUnblockDetails,
		payoutDetailsIsCollapsed,
		setPayoutDetailsIsCollapsed,
		bankDetailsIsCollapsed,
		setBankDetailsIsCollapsed,
		upiDetailsIsCollapsed,
		setUpiDetailsIsCollapsed,
		dataFetchComplete,
		fetchBankDetails,
		fetchUpiDetails,
		downloadBankDetailsCsvFile,
		downloadUpiDetailsCsvFile,
		getTdsDetails,
		getUpiAndBankDetails
	} = useService();
	const { hasPermission: hasPermissionV2 } = usePermissionV2();
	const [forceUpdate, setForceUpdate] = useState(false);

	const turnForceUpdateOff = () => {
		setForceUpdate(false);
	};

	const keyConfig: KeyConfig = {
		childConfig: [
			{
				fetchData: getBasicUserInfo,
				name: VIEW_FIELD_KEYS.USER_IDENTIFICATION_DETAILS,
				label: 'User Identification Details',
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: RESOURCES.basicDetails,
					configActionName: ACTION.getBasicUserDetails
				}),
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.ZUPEE_USER_ID,
						label: 'Zupee User Id'
					},
					{
						name: VIEW_FIELD_KEYS.LUDO_USER_ID,
						label: 'Ludo User Id'
					},
					{
						name: VIEW_FIELD_KEYS.PHONE_NUMBER,
						label: 'Phone Number'
					},
					{
						name: VIEW_FIELD_KEYS.ALTERNATE_PHONE_NUMBER,
						label: 'Alternate Phone Number',
						override: { format: 'button', buttonId: ACTION_BUTTONS_ID.UPDATE_ALTERNATE_PHONE }
					},
					{
						name: VIEW_FIELD_KEYS.EMAIL,
						label: 'Email'
					},
					{
						name: VIEW_FIELD_KEYS.DISPLAY_NAME,
						label: 'Display Name'
					},
					{
						name: VIEW_FIELD_KEYS.GOOGLE_NAME,
						label: 'Google Name'
					},
					{
						name: VIEW_FIELD_KEYS.USER_LEVEL,
						label: 'User Level'
					},
					{
						name: VIEW_FIELD_KEYS.REFERRED_BY,
						label: 'Referred By'
					},
					{
						name: VIEW_FIELD_KEYS.USER_STATUS,
						label: 'User Status',
						override: { format: 'button', buttonId: ACTION_BUTTONS_ID.BLOCK_UNBLOCK_DETAILS }
					},
					{
						name: VIEW_FIELD_KEYS.USER_FRAUD_STATUS,
						label: 'User Fraud Status'
					},
					{
						name: VIEW_FIELD_KEYS.SIGNUP_TIMESTAMP,
						label: 'Signup Timestamp'
					},
					{
						name: VIEW_FIELD_KEYS.USER_SEGMENT,
						label: 'User Segment'
					},
					{
						name: VIEW_FIELD_KEYS.SUBSCRIPTION,
						label: 'Subscription'
					},
					{
						name: VIEW_FIELD_KEYS.KYC_SUMMARY,
						label: 'KYC Summary',
						childConfig: [
							{
								name: VIEW_FIELD_KEYS.KYC_STATUS,
								label: 'KYC Status'
							},
							{
								name: VIEW_FIELD_KEYS.FULL_NAME,
								label: 'Full Name'
							},
							{
								name: VIEW_FIELD_KEYS.DOB,
								label: 'DOB'
							},
							{
								name: VIEW_FIELD_KEYS.IS_MINOR,
								label: 'Is Minor'
							}
						]
					}
				]
			},
			{
				name: VIEW_FIELD_KEYS.USER_WALLET,
				label: 'User Wallet',
				initiallyCollapsed: true,
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: RESOURCES.transactionHistory,
					configActionName: ACTION.getWalletDetails
				}),
				fetchData: getWalletDetails,
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.TOTAL_BALANCE,
						label: 'Total Balance (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.DEPOSIT_MONEY,
						label: 'Deposit Money (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.WINNINGS,
						label: 'Winnings (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.BONUS,
						label: 'Bonus (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.CASHBACK,
						label: 'Cashback (₹)'
					}
				]
			},
			{
				name: VIEW_FIELD_KEYS.TDS,
				label: 'User TDS',
				initiallyCollapsed: true,
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: RESOURCES.basicDetails,
					configActionName: ACTION.getTdsDetails
				}),
				fetchData: getTdsDetails,
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.TDS,
						label: 'Outstanding TDS (₹)'
					}
				]
			}
		],
		name: VIEW_FIELD_KEYS.BASIC_USER_INFO,
		fetchData: _noop
	};

	return {
		keyConfig,
		keyValueLayout: KEY_VALUE_LAYOUT.HORIZONTAL,
		getBlockUnblockDetails,
		payoutDetailsIsCollapsed,
		setPayoutDetailsIsCollapsed,
		bankDetailsIsCollapsed,
		setBankDetailsIsCollapsed,
		upiDetailsIsCollapsed,
		setUpiDetailsIsCollapsed,
		dataFetchComplete,
		fetchBankDetails,
		fetchUpiDetails,
		downloadBankDetailsCsvFile,
		downloadUpiDetailsCsvFile,
		forceUpdate,
		setForceUpdate,
		turnForceUpdateOff,
		getUpiAndBankDetails,
		getBasicUserInfo
	};
};

export default useBasicDetails;
